import { deletesvg } from '@assets/icons'
import Button from '@components/button'
import Column from '@components/column'
import Fields from '@components/fields'
import Grid from '@components/grid'
import Row from '@components/row'
import Svg from '@components/svg'
import Title from '@components/title'
import Wrapper from '@components/wrapper'
import styled from '@emotion/styled'
import theme from '@utils/theme'
import { Form, Formik } from 'formik'
import React, { Fragment, useEffect, useState } from 'react'
import {
  AddButtonWrapper,
  AddOnImage,
  AddOnInfo,
  AddOnsLabel,
  AddOnWrapper,
  FormTopSection,
  outlineButton,
} from './style'
import { useSelector } from 'react-redux'
import { Actions, RootState } from '@store/index'
import { Category, Product } from '@apis/index'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import useNavigateBack from '@hooks/useNavigateBack'

const CreateProduct = () => {
  const [loading, setLoading] = useState(false)
  const goBack = useNavigateBack()
  const [initialValues, setInitialValues] = useState({
    productName: '',
    categoryName: '',
    description: '',
    productImage: '',
    addOns: [],
    addOnIcon: '',
    addOnName: '',
    addOnPoint: '',
  })

  const { state } = useLocation()
  useEffect(() => {
    fetchCateogories()
    if (state) {
      setInitialValues({
        productName: state.name || '',
        categoryName: state.category.id || '',
        description: state.description || '',
        productImage: state.image || '',
        addOns: state.addons || [],
        addOnIcon: '',
        addOnName: '',
        addOnPoint: '',
      })
    }
  }, [state])

  const navigate = useNavigate()
  const { data: categories } = useSelector(
    (state: RootState) => state.categories,
  )
  const categoryOptions =
    categories.map((c) => ({
      title: c.name,
      key: c.id,
    })) || []
  const fetchCateogories = async () => {
    await Category.list({
      state: 'categories',
    })
  }

  const handleSubmit = async (values) => {
    setLoading(true)
    try {
      const payload = {
        name: values.productName,
        description: values.description,
        categoryId: values.categoryName,
        addons: values.addOns,
        image: values.productImage?.base64
          ? {
              base64: values.productImage.base64,
              extension: values.productImage.extension,
            }
          : values.productImage,
      }

      if (!!state) {
        await Product.update({
          id: state.id,
          data: JSON.stringify(payload),
          state: 'products',
        })
        navigate(`/admin/store-management/product`)
      } else {
        await Product.create({
          data: JSON.stringify(payload),
          state: 'products',
        })
        navigate(`/admin/store-management/product`)
      }
    } catch (error) {
      toast.error('Failed to save product')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    fetchCateogories()
  }, [])
  return (
    <Fragment>
      <Column>
        <Title
          textone="Store Management > "
          texttwo="Products >"
          textthree="New Product"
          textTwoClick={goBack}
          textOneClick={goBack}
        />
        <Wrapper>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Column>
                  <FormTopSection>
                    <Grid styleGrid="width:200%" repeat={1}>
                      <Fields.Input
                        name="productName"
                        placeholder="Enter Product Name"
                        label="Product Name"
                      />
                      <Row styleRow="width:97.5%">
                        <Fields.DropDown
                          titleAsKey="title"
                          valueAsKey="key"
                          options={categoryOptions}
                          name="categoryName"
                          placeholder="Enter Category Title"
                          label="Product Category Name"
                          styleWrapper="z-index:999999999;transform:translateX(-12px)"
                        />
                      </Row>
                      <Fields.TextArea
                        name="description"
                        placeholder="Enter Product Description"
                        label="Product Description"
                      />
                    </Grid>
                    <Fields.Upload name="productImage" variant="banner" />
                  </FormTopSection>

                  <AddOnsLabel>Add-Ons</AddOnsLabel>
                  <Grid styleGrid="gap:12px">
                    <Fields.Upload
                      name="addOnIcon"
                      placeholder="Add-On Icon"
                      variant="input"
                    />
                    <Fields.Input name="addOnName" placeholder="Add on Name" />
                    <Fields.Input
                      type="number"
                      name="addOnPoint"
                      placeholder="Add on Points"
                    />
                    <AddButtonWrapper>
                      <Button
                        title="Add"
                        styleButton={outlineButton}
                        height={45}
                        type="button"
                        onClick={() => {
                          if (!values.addOnName || !values.addOnPoint) return
                          const parsedPoint = Number(values.addOnPoint)
                          if (isNaN(parsedPoint)) {
                            toast.error(
                              'Please enter a valid number for Add-On Points',
                            )
                            return
                          }
                          const newAddOn = {
                            icon: {
                              extension: values.addOnIcon.extension,
                              base64: values.addOnIcon.base64,
                            },
                            name: values.addOnName,
                            points: values.addOnPoint,
                          }
                          setFieldValue('addOns', [...values.addOns, newAddOn])
                          setFieldValue('addOnIcon', '')
                          setFieldValue('addOnName', '')
                          setFieldValue('addOnPoint', '')
                        }}
                      />
                    </AddButtonWrapper>
                  </Grid>

                  <Row styleRow="flex-wrap: wrap; gap: 10px;">
                    {values.addOns.map((item, index) => {
                      return (
                        <AddOnWrapper key={index}>
                          <AddOnImage
                            src={
                              item.icon?.base64
                                ? `data:${item.icon.extension};base64,${item.icon.base64}`
                                : `https://admin.tournaop.com/${item.icon}`
                            }
                          />
                          <AddOnInfo>
                            <p className="title">{item.name}</p>
                            <p className="points">{item.points} points</p>
                          </AddOnInfo>
                          <Svg
                            src={deletesvg}
                            onClick={async () => {
                              if (!!state && item.id) {
                                // Updating product => remove from backend
                                try {
                                  await Product.removeAddon({
                                    state: 'products',
                                    id: item.id,
                                  })
                                } catch (error) {
                                  toast.error('Failed to remove add-on')
                                  return
                                }
                              }
                              // Always remove from client side
                              const updated = [...values.addOns]
                              updated.splice(index, 1)
                              setFieldValue('addOns', updated)
                            }}
                          />
                        </AddOnWrapper>
                      )
                    })}
                  </Row>
                  <Row styleRow="justify-content:flex-end">
                    {!!state && (
                      <Button
                        title="Cancel"
                        justify="end"
                        height={42}
                        width={100}
                        type="submit"
                        styleButton="background:red"
                        onClick={() =>
                          navigate(`/admin/store-management/product`)
                        }
                      />
                    )}
                    <Button
                      title={
                        loading
                          ? 'Loading...'
                          : !!state
                            ? 'Update Product'
                            : 'Create Product'
                      }
                      justify="end"
                      height={42}
                      width={100}
                      type="submit"
                    />
                  </Row>
                </Column>
              </Form>
            )}
          </Formik>
        </Wrapper>
      </Column>
    </Fragment>
  )
}

export default CreateProduct
